import React, { useState, useEffect, useRef } from 'react';
import { firestore } from '../../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy, writeBatch } from 'firebase/firestore';
import { logAdminOperation } from '../../lib/adminActivityLogger';
import { Download, Upload } from 'lucide-react';
import Papa from 'papaparse';
import FaqList from '../../components/admin/faq/FaqList';
import FaqForm from '../../components/admin/faq/FaqForm';
import CategoryManager from '../../components/admin/faq/CategoryManager';

function FaqManagement() {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);

  const fileInputRef = useRef(null);

  const categoriesCollection = collection(firestore, 'categories');
  const faqsCollection = collection(firestore, 'faqs');

  const fetchData = async () => {
    setLoading(true);
    const categoryQuery = query(categoriesCollection, orderBy('order'));
    const categorySnapshot = await getDocs(categoryQuery);
    const categoryList = categorySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setCategories(categoryList);

    const faqSnapshot = await getDocs(faqsCollection);
    const faqList = faqSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setFaqs(faqList);

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (faq) => setSelectedFaq(faq);
  const handleCancel = () => setSelectedFaq(null);

  const handleSave = async (faqData) => {
    const { id, ...dataToSave } = faqData;
    try {
      if (id) {
        const faqRef = doc(firestore, 'faqs', id);
        await updateDoc(faqRef, { ...dataToSave, updatedAt: serverTimestamp() });
        
        await logAdminOperation({
          operation_type: 'faq_update',
          target_type: 'faq',
          target_id: id,
          target_name: dataToSave.question || '',
          reason: 'FAQ更新'
        });

        alert("FAQが正常に更新されました！");
      } else {
        const newDoc = await addDoc(faqsCollection, { ...dataToSave, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
        
        await logAdminOperation({
          operation_type: 'faq_create',
          target_type: 'faq',
          target_id: newDoc.id,
          target_name: dataToSave.question || '',
          reason: 'FAQ作成'
        });

        alert("FAQが正常に作成されました！");
      }
      setSelectedFaq(null);
      fetchData();
    } catch (error) {
      console.error("Error saving FAQ: ", error);
      alert("FAQの保存に失敗しました。");
    }
  };

  const handleDelete = async (faqToDelete) => {
    const id = typeof faqToDelete === 'object' && faqToDelete !== null ? faqToDelete.id : faqToDelete;

    if (!id) {
        console.error("Delete failed: Invalid ID provided.", faqToDelete);
        alert("FAQの削除に失敗しました。無効なIDです。");
        return;
    }

    if (window.confirm('このFAQを削除してもよろしいですか？')) {
      try {
        const targetFaq = faqs.find(f => f.id === id);
        await deleteDoc(doc(firestore, 'faqs', id));

        await logAdminOperation({
          operation_type: 'faq_delete',
          target_type: 'faq',
          target_id: id,
          target_name: targetFaq ? targetFaq.question : '',
          reason: 'FAQ削除'
        });

        alert("FAQが正常に削除されました！");
        
        if (selectedFaq && selectedFaq.id === id) {
          setSelectedFaq(null);
        }

        fetchData();
      } catch (error) {
        console.error("Error deleting FAQ: ", error);
        alert("FAQの削除に失敗しました。");
      }
    }
  };

  const handleAddCategory = async (categoryName) => {
    if (!categoryName || !categoryName.trim()) return;
    try {
      const maxOrder = categories.length > 0 ? Math.max(...categories.map(c => c.order || 0)) : 0;
      await addDoc(categoriesCollection, { 
        name: categoryName,
        order: maxOrder + 1
      });
      fetchData();
    } catch (error) {
      console.error("Error adding category: ", error);
      alert("カテゴリの追加に失敗しました。")
    }
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('このカテゴリを削除してもよろしいですか？関連するFAQはカテゴリ未分類になります。')) {
      try {
        await deleteDoc(doc(firestore, 'categories', id));
        fetchData();
      } catch (error) {
        console.error("Error deleting category: ", error);
        alert("カテゴリの削除に失敗しました。")
      }
    }
  };

  const handleMoveCategory = async (index, direction) => {
    const categoryToMove = categories[index];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const targetCategory = categories[targetIndex];

    if (!categoryToMove || !targetCategory) return;

    const currentOrder = categoryToMove.order !== undefined ? categoryToMove.order : index;
    const targetOrder = targetCategory.order !== undefined ? targetCategory.order : targetIndex;

    try {
      const batch = writeBatch(firestore);
      const docRefToMove = doc(firestore, 'categories', categoryToMove.id);
      const docRefTarget = doc(firestore, 'categories', targetCategory.id);

      batch.update(docRefToMove, { order: targetOrder });
      batch.update(docRefTarget, { order: currentOrder });

      await batch.commit();

      const newCategories = [...categories];
      newCategories[index] = { ...targetCategory, order: currentOrder };
      newCategories[targetIndex] = { ...categoryToMove, order: targetOrder };
      setCategories(newCategories);

    } catch (error) {
      console.error("Error moving category: ", error);
      alert("カテゴリの移動に失敗しました。");
      fetchData(); 
    }
  };

  const handleDownloadCsv = () => {
    if (!faqs || faqs.length === 0) {
      alert("ダウンロードするFAQデータがありません。");
      return;
    }

    const escapeCsv = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val);
      return `"${str.replace(/"/g, '""')}"`;
    };

    const headers = ['ID', 'カテゴリ', '表示順', '質問', '回答'];

    const sortedFaqs = [...faqs].sort((a, b) => (a.order || 0) - (b.order || 0));

    const rows = sortedFaqs.map(faq => {
      const categoryStr = Array.isArray(faq.categories)
        ? faq.categories.join(', ')
        : (faq.categories || '');
      const orderVal = faq.order !== undefined && faq.order !== null ? faq.order : '';
      const questionStr = faq.question || '';
      const answerStr = faq.answer || '';

      return [
        escapeCsv(faq.id || ''),
        escapeCsv(categoryStr),
        escapeCsv(orderVal),
        escapeCsv(questionStr),
        escapeCsv(answerStr)
      ].join(',');
    });

    const csvContent = '\uFEFF' + [headers.map(escapeCsv).join(','), ...rows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateStr = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `faq_list_${dateStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const normalizeHeader = (header) => {
    if (!header) return '';
    const h = header.trim().toLowerCase();
    if (h === 'id' || h === 'faq_id' || h === 'id') return 'id';
    if (h === 'カテゴリ' || h === 'category' || h === 'categories') return 'categories';
    if (h === '表示順' || h === 'order') return 'order';
    if (h === '質問' || h === 'question') return 'question';
    if (h === '回答' || h === 'answer') return 'answer';
    return h;
  };

  const handleImportCsv = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: (h) => normalizeHeader(h),
      complete: async (results) => {
        try {
          if (!results.data || results.data.length === 0) {
            alert("インポートするデータが見つかりません。");
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
          }

          const categoryQuery = query(categoriesCollection, orderBy('order'));
          const categorySnapshot = await getDocs(categoryQuery);
          const currentCategories = categorySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

          const faqSnapshot = await getDocs(faqsCollection);
          const currentFaqs = faqSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

          const existingCategoryNames = new Set(currentCategories.map(c => c.name));
          let currentMaxOrder = currentCategories.length > 0 ? Math.max(...currentCategories.map(c => c.order || 0)) : 0;

          const importedCategoryNames = new Set();
          results.data.forEach(row => {
            const catStr = row.categories || '';
            if (catStr) {
              String(catStr).split(/[,、;]/).map(c => c.trim()).filter(Boolean).forEach(c => importedCategoryNames.add(c));
            }
          });

          for (const catName of importedCategoryNames) {
            if (!existingCategoryNames.has(catName)) {
              currentMaxOrder += 1;
              await addDoc(categoriesCollection, {
                name: catName,
                order: currentMaxOrder
              });
              existingCategoryNames.add(catName);
            }
          }

          const idMap = new Map(currentFaqs.map(f => [f.id, f]));
          const questionMap = new Map(currentFaqs.map(f => [f.question ? f.question.trim() : '', f]));

          let createdCount = 0;
          let updatedCount = 0;

          for (const row of results.data) {
            const q = row.question ? String(row.question).trim() : '';
            const a = row.answer ? String(row.answer) : '';
            const rowId = row.id ? String(row.id).trim() : '';

            if (!q && !rowId) continue;

            const catStr = row.categories || '';
            const parsedCategories = catStr
              ? String(catStr).split(/[,、;]/).map(c => c.trim()).filter(Boolean)
              : [];
            const parsedOrder = (row.order !== undefined && row.order !== '' && !isNaN(Number(row.order)))
              ? Number(row.order)
              : 100;

            let targetFaq = null;
            if (rowId && idMap.has(rowId)) {
              targetFaq = idMap.get(rowId);
            } else if (q && questionMap.has(q)) {
              targetFaq = questionMap.get(q);
            }

            if (targetFaq) {
              const faqRef = doc(firestore, 'faqs', targetFaq.id);
              const updateData = {
                question: q || targetFaq.question,
                answer: a,
                categories: parsedCategories,
                order: parsedOrder,
                updatedAt: serverTimestamp()
              };
              await updateDoc(faqRef, updateData);
              await logAdminOperation({
                operation_type: 'faq_update',
                target_type: 'faq',
                target_id: targetFaq.id,
                target_name: q || targetFaq.question,
                reason: 'CSVインポートによるFAQ上書き更新'
              });
              updatedCount++;
            } else {
              const newDocData = {
                question: q,
                answer: a,
                categories: parsedCategories,
                order: parsedOrder,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              };
              const newDoc = await addDoc(faqsCollection, newDocData);
              await logAdminOperation({
                operation_type: 'faq_create',
                target_type: 'faq',
                target_id: newDoc.id,
                target_name: q,
                reason: 'CSVインポートによるFAQ新規作成'
              });
              createdCount++;
            }
          }

          alert(`FAQのインポートが完了しました。（更新: ${updatedCount}件、新規作成: ${createdCount}件）`);
          fetchData();
        } catch (error) {
          console.error("CSV import error:", error);
          alert("CSVのインポート処理中にエラーが発生しました。");
        } finally {
          setImporting(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      },
      error: (err) => {
        console.error("CSV parse error:", err);
        alert("CSVファイルの読み込みに失敗しました。");
        setImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    });
  };

  if (loading) return <div>読み込み中...</div>;

  return (
    <div className="container mx-auto p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">FAQ管理</h1>
        <div className="flex items-center space-x-3">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImportCsv}
            accept=".csv"
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={importing}
            className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 font-bold text-sm shadow-sm transition-colors disabled:opacity-50"
          >
            <Upload className="w-4 h-4 mr-2" />
            {importing ? 'インポート中...' : 'CSVインポート'}
          </button>
          <button
            onClick={handleDownloadCsv}
            className="flex items-center bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 font-bold text-sm shadow-sm transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            CSVダウンロード
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-2xl font-semibold mb-4">FAQの追加・編集</h2>
          <FaqForm 
            faq={selectedFaq} 
            categories={categories}
            onSave={handleSave} 
            onCancel={handleCancel}
          />
        </div>
        <div>
          <h2 className="text-2xl font-semibold mb-4">カテゴリ</h2>
          <CategoryManager 
            categories={categories} 
            onAdd={handleAddCategory}
            onDelete={handleDeleteCategory}
            onMove={handleMoveCategory}
          />
        </div>
      </div>

      <hr className="my-8" />

      <div>
        <h2 className="text-2xl font-semibold mb-4">FAQ一覧</h2>
        <FaqList faqs={faqs} onEdit={handleEdit} onDelete={handleDelete} />
      </div>
    </div>
  );
}

export default FaqManagement;
